import { useRef, useCallback, useEffect } from 'react';
import { useReducedMotion } from 'framer-motion';

const SOUND_FILES = {
  correct: '/sounds/cabin/correct.mp3',
  wrong: '/sounds/cabin/wrong.mp3',
  success: '/sounds/cabin/success.mp3',
  click: '/sounds/cabin/click.mp3',
  levelUp: '/sounds/cabin/level-up.mp3',
  coin: '/sounds/cabin/coin.mp3',
};

const MUTE_KEY = 'cabin_sound_muted';

let HowlClass = null;

async function loadHowl() {
  if (HowlClass) return HowlClass;
  try {
    const mod = await import('howler');
    HowlClass = mod.Howl;
  } catch {
    HowlClass = null;
  }
  return HowlClass;
}

export function useSoundEffects() {
  const shouldReduce = useReducedMotion();
  const soundsRef = useRef({});
  const mutedRef = useRef(
    typeof window !== 'undefined'
      ? localStorage.getItem(MUTE_KEY) === 'true'
      : false
  );

  useEffect(() => {
    if (shouldReduce) return;

    let mounted = true;
    loadHowl().then((Howl) => {
      if (!Howl || !mounted) return;
      Object.entries(SOUND_FILES).forEach(([key, src]) => {
        try {
          soundsRef.current[key] = new Howl({
            src: [src],
            volume: 0.6,
            preload: false,
            onloaderror: () => {
              // Silently ignore missing sound files
            },
          });
        } catch {
          // Silently ignore
        }
      });
    });

    return () => {
      mounted = false;
      Object.values(soundsRef.current).forEach((howl) => {
        try { howl.unload(); } catch { /* noop */ }
      });
      soundsRef.current = {};
    };
  }, [shouldReduce]);

  const play = useCallback((soundKey) => {
    if (shouldReduce) return;
    if (mutedRef.current) return;
    const howl = soundsRef.current[soundKey];
    if (howl) {
      try { howl.play(); } catch { /* noop */ }
    }
  }, [shouldReduce]);

  const setMuted = useCallback((muted) => {
    mutedRef.current = muted;
    try {
      localStorage.setItem(MUTE_KEY, String(muted));
    } catch { /* noop */ }
  }, []);

  const isMuted = useCallback(() => mutedRef.current, []);

  return { play, setMuted, isMuted };
}
